import { NUMBER_PATTERN_TOKENS, SEQUENCE_RESET, modulesOfSeries, numberPatternProblem, type NumberSequenceType } from '@his/shared';
import { tr } from '@/lib/i18n-core';

export const NUMBERING_SERIES: readonly NumberSequenceType[] = [
  'mrn',
  'invoice',
  'estimate',
  'receipt',
  'credit_note',
  'payer_invoice',
  'token',
  'prescription',
  'certificate',
  'accession',
  'admission',
  'er_case',
  'therapy',
  'claim',
  'blood_donor',
  'blood_unit',
  'grn',
  'purchase_order',
  'supplier_payment',
  'purchase_return',
  'stock_take',
  'stock_adjustment',
  'indent',
  'online_order',
  'maintenance_ticket',
  'journal',
  'employee',
];

const SERIES_WITH_HINT: ReadonlySet<NumberSequenceType> = new Set([
  'mrn',
  'estimate',
  'receipt',
  'payer_invoice',
  'token',
  'certificate',
  'accession',
  'blood_donor',
  'blood_unit',
  'maintenance_ticket',
]);

export const PATTERN_TOKEN_KEYS: Readonly<Record<string, string>> = {
  '{SEQ:6}': 'number_token_seq',
  '{YYYY}': 'number_token_yyyy',
  '{YY}': 'number_token_yy',
  '{MM}': 'number_token_mm',
  '{DD}': 'number_token_dd',
  '{BRANCH}': 'number_token_branch',
  '{CHECK}': 'number_token_check',
};

export function seriesState(type: string, modules: readonly string[], retired: readonly string[]): 'live' | 'archived' | 'hidden' {
  const owners = modulesOfSeries(type);
  if (owners.length === 0 || owners.some((module) => modules.includes(module))) return 'live';
  if (owners.some((module) => retired.includes(module))) return 'archived';
  return 'hidden';
}

export function seriesNameKey(type: NumberSequenceType): string {
  return `number_series_${type}`;
}

export function seriesHintKey(type: NumberSequenceType): string | null {
  return SERIES_WITH_HINT.has(type) ? `number_series_${type}_hint` : null;
}

export function seriesName(type: NumberSequenceType): string {
  return tr('admin', seriesNameKey(type));
}

export function seriesHint(type: NumberSequenceType): string | null {
  const key = seriesHintKey(type);
  return key ? tr('admin', key) : null;
}

export function resetKey(type: NumberSequenceType): string {
  const reset = SEQUENCE_RESET[type] ?? 'never';
  return reset === 'never' ? 'never_restarts' : `restarts_${reset}`;
}

export function patternProblemKey(type: NumberSequenceType, pattern: string): { key: string; params?: Record<string, string> } | null {
  const problem = numberPatternProblem(type, pattern);
  if (!problem) return null;
  if (problem.code === 'unknown_token') return { key: 'number_pattern_unknown_token', params: { token: problem.token } };
  if (problem.code === 'no_counter') return { key: 'number_pattern_no_counter', params: { token: '{SEQ}' } };
  return { key: `number_pattern_${problem.code}` };
}

export function patternProblemText(type: NumberSequenceType, pattern: string): string | null {
  const problem = patternProblemKey(type, pattern);
  return problem ? tr('admin', problem.key, problem.params) : null;
}

export function tokenMeaning(token: string): string {
  const key = PATTERN_TOKEN_KEYS[token];
  return key ? tr('admin', key) : (NUMBER_PATTERN_TOKENS.find((entry) => entry.token === token)?.meaning ?? token);
}
